/**
 * The key half of pairing, on the phone.
 *
 * The phone makes its own key pair and proves it holds the private half. The
 * private key never leaves the browser and is never sent anywhere, so the
 * only thing the server ever learns is a public key.
 *
 * P-256 rather than Ed25519: Ed25519 reached WebCrypto only in Safari 17, and
 * this has to work on whatever phone an owner already has. The backend speaks
 * the same curve; services/device_pairing.py says so on its side too.
 */

const CURVE = { name: "ECDSA", namedCurve: "P-256" };
const SIGN = { name: "ECDSA", hash: "SHA-256" };

function bytesToBase64(bytes) {
  let binary = "";
  const view = new Uint8Array(bytes);
  for (let i = 0; i < view.length; i += 1) binary += String.fromCharCode(view[i]);
  return btoa(binary);
}

/**
 * Exactly what the backend signs, character for character.
 *
 * Two implementations of one string is the kind of thing that works in
 * testing and fails on a phone, so this has a test that pins the format
 * rather than only checking that pairing happens to succeed.
 */
export function pairingChallenge(offerId, publicKeySpkiB64) {
  return `novamund-pairing:v1:${offerId}:${publicKeySpkiB64}`;
}

/** A fresh key pair. The private half is not extractable, on purpose. */
export async function generateDeviceKey(subtle) {
  const pair = await subtle.generateKey(CURVE, false, ["sign", "verify"]);
  const spki = await subtle.exportKey("spki", pair.publicKey);
  return { privateKey: pair.privateKey, publicKeySpkiB64: bytesToBase64(spki) };
}

/**
 * WebCrypto returns the signature as raw r||s, which is what the backend
 * expects and converts. Nothing here re-encodes it.
 */
export async function signChallenge(subtle, privateKey, challenge) {
  const signature = await subtle.sign(SIGN, privateKey, new TextEncoder().encode(challenge));
  return bytesToBase64(signature);
}

/** Whether this browser can do the pairing at all, so the UI can say so. */
export function canPair(cryptoObject) {
  return Boolean(cryptoObject && cryptoObject.subtle && typeof cryptoObject.subtle.generateKey === "function");
}

/**
 * The offer and secret arrive in the fragment of the scanned url, not the
 * query. A fragment is never sent to the server and never reaches a log or a
 * Referer header, and this secret is worth exactly one pairing.
 */
export function readPairingFromHash(hash) {
  const params = new URLSearchParams((hash || "").replace(/^#/, ""));
  const offer = params.get("offer");
  const secret = params.get("secret");
  return offer && secret ? { offer, secret } : null;
}
